import React from "react";
import styled from "styled-components";

import {
  brand_orange,
  brand_orange_deep,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import DatePicker from "../../components/DatePicker";
import SearchableSelect from "../../components/SearchableSelect";
import SearchBox from "../../components/SearchBox";

// Advance-filter panel for the /webinars catalog. Every control maps to one
// whitelisted param on /api/v1/webinars, and the parent hook reflects state into
// the URL, so this component stays presentational.
//
// Only the free-text box is debounced (useDebouncedSearch); every other control
// here is a single deliberate action and applies immediately.

const STATUS_OPTIONS = [
  { value: "all", label: "Semua" },
  { value: "upcoming", label: "Akan Datang" },
  { value: "past", label: "Selesai" },
];

const TYPE_OPTIONS = [
  { value: "", label: "Semua Tipe" },
  { value: "free", label: "Gratis" },
  { value: "general", label: "Umum" },
  { value: "special", label: "Khusus" },
];

const SORT_OPTIONS = [
  { value: "date_desc", label: "Terbaru" },
  { value: "date_asc", label: "Terlama" },
  { value: "title_asc", label: "Judul A–Z" },
  { value: "capacity", label: "Kapasitas Terbesar" },
];

// Server ships vocabulary options as { slug, name, count }; SearchableSelect
// speaks { value, label, count }.
function toSelectOptions(options) {
  return (options || []).map((o) => ({
    value: o.slug,
    label: o.name,
    count: o.count,
  }));
}

function isoToDate(iso) {
  return iso ? new Date(`${iso}T00:00:00`) : null;
}

function dateToIso(date) {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const Panel = styled.div`
  background: #f9fafb;
  border: 1px solid #eef0f2;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &[data-theme="dark"],
  [data-theme="dark"] & {
    background: #17171e;
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

// The input itself lives in <SearchBox>, which owns its own draft state; this
// only reserves the slot in the flex row.
const SearchSlot = styled.div`
  flex: 1 1 260px;
  min-width: 0;
  display: flex;
`;

const Segmented = styled.div`
  background: #eef0f2;
  border-radius: 10px;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 3px;
  padding: 3px;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const SegButton = styled.button`
  border: none;
  background: ${({ $active }) => ($active ? "#fff" : "transparent")};
  color: ${({ $active }) => ($active ? brand_orange_deep : "#6b7280")};
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: ${({ $active }) =>
    $active ? "0 1px 2px rgba(0,0,0,0.08)" : "none"};
  transition: all 150ms ease;

  &:focus-visible {
    outline: 2px solid ${brand_orange};
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    background: ${({ $active }) => ($active ? "#1f2937" : "transparent")};
    color: ${({ $active }) => ($active ? "#fff" : "#9ca3af")};
  }
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid
    ${({ $active }) => ($active ? brand_orange_deep : "#d1d5db")};
  background: ${({ $active }) => ($active ? "rgba(216,89,7,0.08)" : "#fff")};
  color: ${({ $active }) => ($active ? brand_orange_deep : "#4b5563")};
  padding: 9px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 150ms ease;

  &:focus-visible {
    outline: 2px solid ${brand_orange};
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    background: ${({ $active }) =>
      $active ? "rgba(216,89,7,0.16)" : "#0f172a"};
    border-color: ${({ $active }) =>
      $active ? brand_orange_deep : "rgba(255,255,255,0.14)"};
    color: ${({ $active }) => ($active ? "#fdba74" : "#d1d5db")};
  }
`;

const DateFields = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .react-datepicker-wrapper {
    width: 130px;
  }

  input {
    width: 130px;
    height: 44px;
    padding: 0 12px;
    border-radius: 10px;
    border: 1px solid #d1d5db;
    background: #fff;
    font-size: 13px;
    color: #1f2937;
  }

  @media ${narrowScreenMediaQuery} {
    flex-wrap: wrap;
  }
`;

const DateSep = styled.span`
  color: #9ca3af;
  font-size: 13px;
`;

const ResetButton = styled.button`
  border: none;
  background: transparent;
  color: ${brand_orange_deep};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 8px 4px;
  text-decoration: underline;

  &:focus-visible {
    outline: 2px solid ${brand_orange};
    outline-offset: 2px;
  }
`;

function FilterBar({
  filters,
  update,
  reset,
  activeFilterCount,
  signedIn,
  themeOptions = [],
  typeNameOptions = [],
  // Search is delegated to <SearchBox>, which keeps the draft in its own state so
  // typing does not re-render this panel or the results grid. `commitSearch` must
  // be referentially stable.
  commitSearch,
  searchResetToken,
  loading = false,
}) {
  return (
    <Panel>
      <Row>
        <SearchSlot>
          <SearchBox
            id="webinar-search"
            initialValue={filters.q}
            onCommit={commitSearch}
            resetToken={searchResetToken}
            placeholder="Cari webinar, topik, atau pembicara…"
            ariaLabel="Cari webinar"
            loading={loading}
          />
        </SearchSlot>
        <Group>
          <FieldLabel>Urutkan</FieldLabel>
          {/* Same control as the vocabulary filters so the three dropdowns
           * read as one family. Sort always has a value, so no "all" row.
           */}
          <SearchableSelect
            value={filters.sort}
            onChange={(next) => update({ sort: next || "date_desc" })}
            options={SORT_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            showAll={false}
            placeholder="Terbaru"
            ariaLabel="Urutkan webinar"
          />
        </Group>
      </Row>

      <Row>
        <Group>
          <FieldLabel>Status</FieldLabel>
          <Segmented role="group" aria-label="Filter status">
            {STATUS_OPTIONS.map((o) => (
              <SegButton
                key={o.value}
                type="button"
                $active={filters.status === o.value}
                aria-pressed={filters.status === o.value}
                onClick={() => update({ status: o.value })}
              >
                {o.label}
              </SegButton>
            ))}
          </Segmented>
        </Group>

        <Group>
          {/* webinar_type (free/general/special) — labelled "Akses" so it is
           * not confused with the "Nama Tipe" vocabulary below.
           */}
          <FieldLabel>Akses</FieldLabel>
          <Segmented role="group" aria-label="Filter akses">
            {TYPE_OPTIONS.map((o) => (
              <SegButton
                key={o.value || "all"}
                type="button"
                $active={filters.type === o.value}
                aria-pressed={filters.type === o.value}
                onClick={() => update({ type: o.value })}
              >
                {o.label}
              </SegButton>
            ))}
          </Segmented>
        </Group>
      </Row>

      <Row>
        <Group>
          <FieldLabel>Tema</FieldLabel>
          <SearchableSelect
            value={filters.theme}
            onChange={(next) => update({ theme: next })}
            options={toSelectOptions(themeOptions)}
            allLabel="Semua Tema"
            placeholder="Semua Tema"
            searchPlaceholder="Cari tema…"
            ariaLabel="Filter tema webinar"
          />
        </Group>

        <Group>
          <FieldLabel>Nama Tipe</FieldLabel>
          <SearchableSelect
            value={filters.type_name}
            onChange={(next) => update({ type_name: next })}
            options={toSelectOptions(typeNameOptions)}
            allLabel="Semua Nama Tipe"
            placeholder="Semua Nama Tipe"
            searchPlaceholder="Cari nama tipe…"
            ariaLabel="Filter nama tipe webinar"
          />
        </Group>
      </Row>

      <Row>
        <Toggle
          type="button"
          $active={filters.has_recording}
          aria-pressed={filters.has_recording}
          onClick={() => update({ has_recording: !filters.has_recording })}
        >
          ▶ Punya Rekaman
        </Toggle>
        {signedIn && (
          <Toggle
            type="button"
            $active={filters.registered}
            aria-pressed={filters.registered}
            onClick={() => update({ registered: !filters.registered })}
          >
            ✓ Sudah Terdaftar
          </Toggle>
        )}

        <Group>
          <FieldLabel>Rentang Tanggal</FieldLabel>
          <DateFields>
            <DatePicker
              selected={isoToDate(filters.date_from)}
              onChange={(date) => update({ date_from: dateToIso(date) })}
              dateFormat="dd/MM/yyyy"
              placeholderText="Dari"
              isClearable
              maxDate={isoToDate(filters.date_to)}
              aria-label="Tanggal dari"
            />
            <DateSep>—</DateSep>
            <DatePicker
              selected={isoToDate(filters.date_to)}
              onChange={(date) => update({ date_to: dateToIso(date) })}
              dateFormat="dd/MM/yyyy"
              placeholderText="Sampai"
              isClearable
              minDate={isoToDate(filters.date_from)}
              aria-label="Tanggal sampai"
            />
          </DateFields>
        </Group>

        {activeFilterCount > 0 && (
          <ResetButton type="button" onClick={reset}>
            Reset filter ({activeFilterCount})
          </ResetButton>
        )}
      </Row>
    </Panel>
  );
}

// Memoized: this panel holds ~20 styled controls and must not re-render when
// results land. Its props are stable apart from `filters` (which genuinely
// changes) and `loading`.
export default React.memo(FilterBar);
